import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import NodeAppBar from "./NodeAppBar";
import ChildrenRenderer from "../Nodes/ChildrenRenderer";
import { getRevAllNodesWithStatus } from "../../../../redux/procedureRevisionSlice";
import Loader from "../../../../common/Loader";
import { setTaskReference } from "../../../Tasks/Uitlities";
import { isLocked } from "../../Utils/utils";
import axios from "axios";
import { BASE_URL } from "../../../../config";
import { lockTask, pingTask } from "../../../../util/apiService";
import { clearCanvasElementStores } from "../../../../util/indexedDBUtils";
import { showSnackbar } from "../../../../redux/snackbarSlice";
import useApiTabLock from "../../../../hooks/useApiTabLock";
import { useRevisionLock } from "../../../../contexts/RevisionLockContext";

/**
 * ViewChildNode Component
 *
 * Displays child node details when user checks the checkbox before node name
 * Route: /view-childnode/:partnumber/:revision/:partId/:revisionId/:section_id_ref/:step_id_ref/:child_node_id
 * Query params: substep_id_ref (optional - only when node is child of substep)
 */
function versionHistoryNodeIdFieldForChildType(type) {
  switch (type) {
    case "attachment":
      return "attachment_tree_node_id";
    case "video":
      return "video_tree_node_id";
    case "rich-text":
      return "rich_text_node_id";
    case "canva":
      return "canvas_tree_node_id";
    case "form":
      return "form_tree_node_id";
    default:
      return "tree_node_id";
  }
}

/** Regex fragment for {@link rowMatchesLeafMediaAuditOrdinal} — matches audit titles like "Save Attachment 3". */
function leafMediaPhraseRegexForChildType(type) {
  switch (type) {
    case "attachment":
      return "Attachment";
    case "video":
      return "Video";
    case "rich-text":
      return "Rich(?:\\s+Text)?";
    case "canva":
      return "Canvas";
    case "form":
      return "Form";
    default:
      return null;
  }
}

/** Same traversal as on-screen "Attachment N" / media numbering (ChildrenRenderer). */
function computeGlobalMediaOrdinalForChild({
  allRevisionNodes,
  section_id_ref,
  step_id_ref,
  child_node_id,
  nodeType,
}) {
  if (
    !nodeType ||
    !child_node_id ||
    !Array.isArray(allRevisionNodes) ||
    !section_id_ref ||
    !step_id_ref
  ) {
    return null;
  }

  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...allRevisionNodes].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  let counter = 0;
  const matchesId = (c) =>
    c.tree_node_id === child_node_id ||
    c.detail?.canvas_tree_node_id === child_node_id ||
    c.detail?.attachment_tree_node_id === child_node_id;

  for (let i = 0; i < sortedSections.length; i++) {
    const section = sortedSections[i];
    for (let j = 0; j < (section.steps?.length || 0); j++) {
      const step = section.steps[j];
      const stepChildren =
        step.children?.filter((c) => c.type === nodeType) || [];

      const currentStepChild = stepChildren.find(matchesId);
      if (currentStepChild) {
        const sortedStepChildren = [...stepChildren].sort((a, b) => {
          const orderA = a.sibling_order || 0;
          const orderB = b.sibling_order || 0;
          return orderA - orderB;
        });
        const currentChildIndex = sortedStepChildren.findIndex(matchesId);
        counter += currentChildIndex;
        return counter + 1;
      }
      counter += stepChildren.length;

      for (let k = 0; k < (step.substeps?.length || 0); k++) {
        const substep = step.substeps[k];
        const substepChildren =
          substep.children?.filter((c) => c.type === nodeType) || [];
        const currentSubstepChild = substepChildren.find(matchesId);
        if (currentSubstepChild) {
          const sortedSubstepChildren = [...substepChildren].sort((a, b) => {
            const orderA = a.sibling_order || 0;
            const orderB = b.sibling_order || 0;
            return orderA - orderB;
          });
          const currentChildIndex =
            sortedSubstepChildren.findIndex(matchesId);
          counter += currentChildIndex;
          return counter + 1;
        }
        counter += substepChildren.length;
      }
    }
  }
  return counter + 1;
}

const ViewChildNode = () => {
  const {
    partnumber,
    revision,
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    child_node_id,
  } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const searchParams = new URLSearchParams(location.search);
  const is_deleted = searchParams.get("is_deleted");
  const substep_id_ref = searchParams.get("substep_id_ref");

  const { allRevisionNodes, currentStatus } = useSelector(
    (state) => state.procedureRevision
  );
  const authUser = useSelector((state) => state.auth.user);

  const [loading, setLoading] = useState(true);
  const [canvasData, setCanvasData] = useState(null);
  const [pingInterval, setPingInterval] = useState(null);

  // State to track if we should temporarily bypass tab order lock
  // Initialize by checking if THIS specific tab has a bypass flag
  const [bypassTabOrderLock, setBypassTabOrderLock] = useState(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const bypassFlag = sessionStorage.getItem(`tabOrderLockBypass_${revisionId}_${tabId}`);
    return bypassFlag === 'true';
  });

  // Use revision ID as route key to lock entire revision across all pages
  // This ensures all pages within the same revision (Document, sections, steps, substeps, canvases, etc.)
  // share the same lock across tabs
  const routeKey = `revision_${revisionId}`;

  // Tab lock management - only one tab can perform actions
  // Use revision-specific key to ensure all pages within the same revision share the same lock
  const { isLocked: isApiLocked, isActiveTab, lockedBy } = useApiTabLock(`revision_${revisionId}`);

  // Tab order lock from provider
  const revisionLock = useRevisionLock();
  const isFirstTabByOrder =
    revisionLock?.revisionIdFromPath === revisionId ? revisionLock.isFirstTab : false;
  const isLockedByOrder =
    revisionLock?.revisionIdFromPath === revisionId ? revisionLock.isLocked : true;

  // Leadership restoration - Check if this tab was the leader before navigation
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    const fromDocument = sessionStorage.getItem(`fromDocument_${revisionId}`) === 'true';
    const wasLeader = sessionStorage.getItem(`wasLeader_${revisionId}_${tabId}`) === 'true';

    if (fromDocument && wasLeader) {
      setBypassTabOrderLock(true);
      sessionStorage.setItem(`tabOrderLockBypass_${revisionId}_${tabId}`, 'true');
    }
  }, [revisionId]);

  // Track when this tab becomes the leader
  useEffect(() => {
    const tabId = sessionStorage.getItem('tab-id');
    if (isFirstTabByOrder) {
      sessionStorage.setItem(`wasLeader_${revisionId}_${tabId}`, 'true');
    }
  }, [isFirstTabByOrder, revisionId]);

  const isLock = isLocked(currentStatus);

  // Helper function to normalize shape type (matching useCanvasPersistence.js)
  const normalizeShapeType = (type) => {
    if (!type) return null;
    switch (type.toLowerCase()) {
      case "arrow":
        return "Arrow";
      case "double_sided_arrow":
      case "double-sided-arrow":
        return "DoubleSidedArrow";
      case "line":
        return "Line";
      case "rect":
      case "rectangle":
        return "Rect";
      case "circle":
        return "Circle";
      case "text":
        return "Text";
      case "image":
        return "Image";
      default:
        return type;
    }
  };

  // Determine if this is a substep child (substep_id_ref will be null for step children)
  const isSubstepChild = substep_id_ref && substep_id_ref !== "";

  // Derive Data from Redux Store
  const sectionData = useMemo(() => {
    return allRevisionNodes?.find((section) => section.tree_node_id === section_id_ref);
  }, [allRevisionNodes, section_id_ref]);

  const stepData = useMemo(() => {
    return sectionData?.steps?.find((step) => step.tree_node_id === step_id_ref);
  }, [sectionData, step_id_ref]);

  const childNodeData = useMemo(() => {
    let childNode = null;
    if (stepData) {
      if (isSubstepChild && substep_id_ref) {
        // Find substep and its children
        const currentSubstep = stepData.substeps?.find(
          (substep) => substep.tree_node_id === substep_id_ref
        );
        if (currentSubstep) {
          childNode = currentSubstep.children?.find(
            (child) =>
              child.tree_node_id === child_node_id ||
              child.detail?.canvas_tree_node_id === child_node_id ||
              child.detail?.attachment_tree_node_id === child_node_id
          );
        }
      } else {
        // Find child in step's children
        childNode = stepData.children?.find(
          (child) =>
            child.tree_node_id === child_node_id ||
            child.detail?.canvas_tree_node_id === child_node_id ||
            child.detail?.attachment_tree_node_id === child_node_id
        );
      }
    }
    return childNode;
  }, [stepData, child_node_id, isSubstepChild, substep_id_ref]);

  const childVersionHistoryMatchIds = useMemo(() => {
    const fromAttachments = Array.isArray(childNodeData?.detail?.attachments)
      ? childNodeData.detail.attachments.flatMap((a) =>
          [a?.attachment_tree_node_ref, a?.attachment_id].filter(Boolean),
        )
      : [];
    return [
      child_node_id,
      childNodeData?.tree_node_id,
      childNodeData?.detail?.procedure_tree_ref,
      childNodeData?.attachment_id_ref,
      childNodeData?.attachment_tree_node_id,
      childNodeData?.detail?.attachment_id,
      childNodeData?.detail?.attachment_tree_node_id,
      childNodeData?.detail?.video_tree_node_id,
      childNodeData?.detail?.rich_text_node_id,
      childNodeData?.detail?.rich_text_tree_node_id,
      childNodeData?.detail?.canvas_tree_node_id,
      childNodeData?.detail?.canva_tree_node_id,
      childNodeData?.detail?.form_tree_node_id,
      childNodeData?.detail?.element_id,
      ...fromAttachments,
    ].filter(Boolean);
  }, [
    child_node_id,
    childNodeData?.tree_node_id,
    childNodeData?.detail?.procedure_tree_ref,
    childNodeData?.attachment_id_ref,
    childNodeData?.attachment_tree_node_id,
    childNodeData?.detail?.attachment_id,
    childNodeData?.detail?.attachment_tree_node_id,
    childNodeData?.detail?.video_tree_node_id,
    childNodeData?.detail?.rich_text_node_id,
    childNodeData?.detail?.rich_text_tree_node_id,
    childNodeData?.detail?.canvas_tree_node_id,
    childNodeData?.detail?.canva_tree_node_id,
    childNodeData?.detail?.form_tree_node_id,
    childNodeData?.detail?.element_id,
    childNodeData?.detail?.attachments,
  ]);

  const childMediaOrdinalForNodeHistory = useMemo(
    () =>
      computeGlobalMediaOrdinalForChild({
        allRevisionNodes,
        section_id_ref,
        step_id_ref,
        child_node_id,
        nodeType: childNodeData?.type,
      }),
    [
      allRevisionNodes,
      section_id_ref,
      step_id_ref,
      child_node_id,
      childNodeData?.type,
    ],
  );

  const childLeafMediaPhraseRegexForNodeHistory = useMemo(
    () => leafMediaPhraseRegexForChildType(childNodeData?.type),
    [childNodeData?.type],
  );

  // Helper function to fetch canvas data using get-by-id API
  const fetchCanvasData = async () => {
    try {
      const canvasNodeId = childNodeData?.detail?.canvas_tree_node_id;
      if (canvasNodeId) {
        const response = await axios.get(
          `${BASE_URL}canvas-tree/get-by-id/${canvasNodeId}`
        );
        const data = response.data.results;
        setCanvasData(data);
      }
    } catch (error) {
      console.error("Error fetching canvas data:", error);
    }
  };

  useEffect(() => {
    const fetchNodes = async () => {
      try {
        setLoading(true);
        await dispatch(
          getRevAllNodesWithStatus({
            partId: partId,
            revisionId: revisionId,
            is_deleted: is_deleted
          })
        );
      } catch (error) {
        console.error("Error fetching revision nodes:", error);
      } finally {
        setLoading(false);
      }
    };

    if (
      partId &&
      revisionId &&
      section_id_ref &&
      step_id_ref &&
      child_node_id
    ) {
      fetchNodes();
    }
  }, [
    partId,
    revisionId,
    section_id_ref,
    step_id_ref,
    substep_id_ref,
    child_node_id,
    isSubstepChild,
    dispatch,
    is_deleted,
  ]);

  // Separate useEffect for fetching canvas data after childNodeData is available
  useEffect(() => {
    if (childNodeData?.type === "canva" && childNodeData?.detail?.canvas_tree_node_id) {
      fetchCanvasData();
    }
  }, [childNodeData]);

  // Enhanced childNodeData that includes canvas data for proper rendering
  const enhancedChildNodeData = useMemo(() => {
    if (!childNodeData) return null;

    // For canvas nodes, merge canvas data from API
    if (childNodeData.type === "canva" && canvasData) {
      return {
        ...childNodeData,
        detail: {
          ...childNodeData.detail,
          canvas_json_data_file: canvasData.canvas_json_data_file,
          // Add any other canvas-specific data from the API response
          pages: canvasData.pages,
          short_description: canvasData.short_description,
        }
      };
    }

    return childNodeData;
  }, [childNodeData, canvasData]);

  // Helper function to format node type name for display
  const getNodeTypeName = (type) => {
    switch (type) {
      case "rich-text":
        return "Rich Text";
      case "video":
        return "Video";
      case "form":
        return "Form";
      case "canva":
        return "Canvas";
      case "attachment":
        return "Attachment";
      default:
        return type;
    }
  };

  // Helper function to get the specific tree_node_id for this child node type
  const getChildNodeReferenceId = (childNodeData) => {
    if (!childNodeData) return null;
    const type = childNodeData?.type;
    const detail = childNodeData?.detail;

    switch (type) {
      case "attachment":
        return detail?.attachment_tree_node_id;
      case "video":
        return detail?.video_tree_node_id;
      case "rich-text":
        return detail?.rich_text_node_id;
      case "canva":
        return detail?.canvas_tree_node_id;
      case "form":
        return detail?.form_tree_node_id;
      default:
        return null;
    }
  };

  // Set task reference for this child node - only when data changes
  // Must be called before any early returns (React Hooks rule)
  useEffect(() => {
    if (enhancedChildNodeData && partnumber) {
      const nodeTypeName = getNodeTypeName(enhancedChildNodeData?.type);
      setTaskReference(
        dispatch,
        // `${nodeTypeName} ${nodeNumber}, ${partnumber} Rev ${revision}`,
        `${nodeTypeName} ${partnumber}`,
        getChildNodeReferenceId(enhancedChildNodeData),
        enhancedChildNodeData?.tree_node_id,
        enhancedChildNodeData?.ltree_path
      );
    }
  }, [enhancedChildNodeData, partnumber, dispatch]);

  const onNavigate = async (nodeType, nodeData, context) => {
    // Call lock API when checkbox is clicked
    try {
      if (authUser && enhancedChildNodeData) {
        const lockData = {
          task_id: revisionId, // Use revision ID as task_id
          user_id: authUser.id,
          user_name: `${authUser.first_name} ${authUser.last_name}`,
          action: "edit"
        };

        await lockTask(lockData);
        dispatch(showSnackbar({
          message: "Child node locked successfully",
          severity: "success"
        }));
        // Start ping interval after successful lock
        startPingInterval();
      }
    } catch (error) {
      console.error('Failed to lock child node:', error);
      dispatch(showSnackbar({
        message: "Failed to lock child node",
        severity: "error"
      }));
    }
  };

  // Start ping interval function
  const startPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
    }

    const interval = setInterval(async () => {
      try {
        if (authUser) {
          const pingData = {
            task_id: revisionId,
            user_id: authUser.id
          };
          await pingTask(pingData);
        }
      } catch (error) {
        console.error('Ping failed:', error);
      }
    }, 60000); // 60 seconds = 1 minute

    setPingInterval(interval);
  };

  // Stop ping interval function
  const stopPingInterval = () => {
    if (pingInterval) {
      clearInterval(pingInterval);
      setPingInterval(null);
    }
  };

  // Cleanup ping interval on component unmount
  useEffect(() => {
    return () => {
      stopPingInterval();
    };
  }, [pingInterval]);

  const onAddElement = (elementType) => {
    if (elementType === "Attachment") {
      // Build the base URL for adding attachment
      const baseUrl = `/add-new-attachment/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`;

      // Add query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("node_id_ref", child_node_id);

      // Add substep_id_ref if the current node is within a substep
      if (isSubstepChild && substep_id_ref) {
        queryParams.append("substep_id_ref", substep_id_ref);
      }

      const fullUrl = `${baseUrl}?${queryParams.toString()}`;
      navigate(fullUrl);
    } else if (elementType === "Video") {
      // Build the base URL for adding video
      const baseUrl = `/add-new-video/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`;

      // Add query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("node_id_ref", child_node_id);

      // Add substep_id_ref if the current node is within a substep
      if (isSubstepChild && substep_id_ref) {
        queryParams.append("substep_id_ref", substep_id_ref);
      }

      const fullUrl = `${baseUrl}?${queryParams.toString()}`;
      navigate(fullUrl);
    } else if (elementType === "Rich Text") {
      // Build the base URL for adding rich text
      const baseUrl = `/add-new-richtext/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`;

      // Add query parameters
      const queryParams = new URLSearchParams();
      queryParams.append("node_id_ref", child_node_id);

      // Add substep_id_ref if the current node is within a substep
      if (isSubstepChild && substep_id_ref) {
        queryParams.append("substep_id_ref", substep_id_ref);
      }

      const fullUrl = `${baseUrl}?${queryParams.toString()}`;
      navigate(fullUrl);
    } else if (elementType === "Canvas") {
      // Clear all canvas-related storage before adding
      const clearAllCanvasData = async () => {
        try {
          // 1. Clear localStorage items
          localStorage.removeItem("canvasPages");
          // Ensure all add-mode draft keys are cleared
          localStorage.removeItem("canvas_pages");
          localStorage.removeItem("canvas_title");
          localStorage.removeItem("canvas_description");
          localStorage.removeItem("canvasSnapshot");
          localStorage.removeItem("canvasState");
          localStorage.removeItem("short_description");
          localStorage.removeItem("editCanvasReturningFromPreview");
          // CRITICAL: Clear fromMyCanvas flag when navigating from ViewChildNode to Canvas
          localStorage.removeItem("fromMyCanvas");
          // CRITICAL: Set preventApiCall flag to false to ensure API is called when going to CanvasForm for first time
          localStorage.setItem('preventApiCall', 'false');
          // 2. Clear sessionStorage items
          sessionStorage.removeItem("canvasStateInfo");
          // 3. Clear IndexedDB canvas element stores
          try {
            await clearCanvasElementStores();
          } catch (error) {
            console.warn("⚠️ Error clearing canvasElementsDB stores:", error);
          }

          // 4. Clear IndexedDB - CanvasStorageDB
          try {
            const canvasStorageDB = await new Promise((resolve, reject) => {
              const req = indexedDB.open("CanvasStorageDB");
              req.onsuccess = (e) => resolve(e.target.result);
              req.onerror = (e) => reject(e.target.error);
            });

            if (
              canvasStorageDB &&
              canvasStorageDB.objectStoreNames.contains("canvasStorage")
            ) {
              const transaction = canvasStorageDB.transaction(
                ["canvasStorage"],
                "readwrite"
              );
              const store = transaction.objectStore("canvasStorage");
              await new Promise((resolve, reject) => {
                const clearRequest = store.clear();
                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(clearRequest.error);
              });
              canvasStorageDB.close();
            }
          } catch (error) {
            console.warn("⚠️ Error clearing CanvasStorageDB:", error);
          }

        } catch (error) {
          console.error("❌ Failed to clear canvas data for Add mode:", error);
        }
      };

      // Wait for clearing to complete before navigating
      clearAllCanvasData().then(() => {
        // Build the base URL for adding canvas
        const baseUrl = `/add-new-canvas/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}`;

        // Add query parameters
        const queryParams = new URLSearchParams();
        queryParams.append("node_id_ref", child_node_id);

        // Add substep_id_ref if the current node is within a substep
        if (isSubstepChild && substep_id_ref) {
          queryParams.append("substep_id_ref", substep_id_ref);
        }

        const fullUrl = `${baseUrl}?${queryParams.toString()}`;
        navigate(fullUrl, {
          state: {
            returnTo: `${location.pathname}${location.search || ""}`,
          },
        });
      });
    } else {
      // Handle other element types if needed
    }
  };

  const onEditSectionElement = () => {
    // Check if the current node is an attachment
    if (enhancedChildNodeData?.type === "attachment") {
      // Build the edit URL for attachment
      const baseUrl = `/edit-attachment/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${child_node_id}`;

      // Add substep_id_ref as query parameter if the attachment is in a substep
      if (isSubstepChild && substep_id_ref) {
        const queryParams = new URLSearchParams();
        queryParams.append("substep_id_ref", substep_id_ref);
        const fullUrl = `${baseUrl}?${queryParams.toString()}`;
        navigate(fullUrl);
      } else {
        navigate(baseUrl);
      }
    } else if (enhancedChildNodeData?.type === "video") {
      // Build the edit URL for video
      const baseUrl = `/edit-video/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${child_node_id}`;

      // Add substep_id_ref as query parameter if the video is in a substep
      if (isSubstepChild && substep_id_ref) {
        const queryParams = new URLSearchParams();
        queryParams.append("substep_id_ref", substep_id_ref);
        const fullUrl = `${baseUrl}?${queryParams.toString()}`;
        navigate(fullUrl);
      } else {
        navigate(baseUrl);
      }
    } else if (enhancedChildNodeData?.type === "rich-text") {
      // Build the edit URL for rich text
      const baseUrl = `/edit-rich-text/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${child_node_id}`;

      // Add substep_id_ref as query parameter if the rich text is in a substep
      if (isSubstepChild && substep_id_ref) {
        const queryParams = new URLSearchParams();
        queryParams.append("substep_id_ref", substep_id_ref);
        const fullUrl = `${baseUrl}?${queryParams.toString()}`;
        navigate(fullUrl);
      } else {
        navigate(baseUrl);
      }
    } else if (enhancedChildNodeData?.type === "canva") {
      // Clear all canvas-related storage before editing
      // This ensures we start fresh with API data only
      const clearAllCanvasData = async () => {
        try {
          // 1. Clear localStorage items
          localStorage.removeItem("canvasPages");
          localStorage.removeItem("canvas_pages");
          localStorage.removeItem("originalCanvasPages");
          localStorage.removeItem("canvasSnapshot");
          localStorage.removeItem("canvasState");
          localStorage.removeItem("short_description");

          // CRITICAL: Clear all unsaved changes flags to prevent false positives
          localStorage.removeItem("fromMyCanvas");
          localStorage.removeItem("canvasChangesDetected");
          localStorage.removeItem("removedCanvasPages");

          // Clear canvas-specific unsaved changes flags (use child_node_id as canvas ID)
          // CRITICAL: Clear the baseline when starting a fresh edit session
          // This ensures we capture a new baseline from the current API data
          if (child_node_id) {
            localStorage.removeItem(`canvasHasUnsavedChanges_${child_node_id}`);
            // Clear the baseline to force a fresh capture from current API data
            localStorage.removeItem(`initialApiPages_${child_node_id}`);
          }

          // CRITICAL: Set preventApiCall flag to false to ensure API is called when editing canvas
          localStorage.setItem('preventApiCall', 'false');
          // 2. Clear sessionStorage items
          sessionStorage.removeItem("canvasStateInfo");
          // 3. Clear IndexedDB canvas element stores
          try {
            await clearCanvasElementStores();
          } catch (error) {
            console.warn("⚠️ Error clearing canvasElementsDB stores:", error);
          }

          // 4. Clear IndexedDB - CanvasStorageDB
          try {
            const canvasStorageDB = await new Promise((resolve, reject) => {
              const req = indexedDB.open("CanvasStorageDB");
              req.onsuccess = (e) => resolve(e.target.result);
              req.onerror = (e) => reject(e.target.error);
            });

            if (
              canvasStorageDB &&
              canvasStorageDB.objectStoreNames.contains("canvasStorage")
            ) {
              const transaction = canvasStorageDB.transaction(
                ["canvasStorage"],
                "readwrite"
              );
              const store = transaction.objectStore("canvasStorage");
              await new Promise((resolve, reject) => {
                const clearRequest = store.clear();
                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(clearRequest.error);
              });
              canvasStorageDB.close();
            }
          } catch (error) {
            console.warn("⚠️ Error clearing CanvasStorageDB:", error);
          }

        } catch (error) {
          console.error("❌ Failed to clear canvas data for Edit mode:", error);
        }
      };

      // Wait for clearing to complete before navigating
      clearAllCanvasData().then(() => {
        // Build the edit URL for canvas
        const baseUrl = `/edit-canvas-new/${partnumber}/${revision}/${partId}/${revisionId}/${section_id_ref}/${step_id_ref}/${child_node_id}`;

        // Add substep_id_ref as query parameter if the canvas is in a substep
        if (isSubstepChild && substep_id_ref) {
          const queryParams = new URLSearchParams();
          queryParams.append("substep_id_ref", substep_id_ref);
          const fullUrl = `${baseUrl}?${queryParams.toString()}`;
          navigate(fullUrl, {
            state: {
              forceFreshEditLoad: true,
              returnTo: `${location.pathname}${location.search || ""}`,
            },
          });
        } else {
          navigate(baseUrl, {
            state: {
              forceFreshEditLoad: true,
              returnTo: `${location.pathname}${location.search || ""}`,
            },
          });
        }
      });
    } else {
      // Handle other element types if needed
    }
  };

  if (loading) return <Loader text={`Loading details...`} />;

  if (!enhancedChildNodeData) {
    return (
      <div style={{ padding: "20px" }}>
        <p>Child node not found.</p>
      </div>
    );
  }

  // Calculate indices from data
  const compareSiblingOrders = (a, b) => {
    const intA = parseInt(a, 10);
    const intB = parseInt(b, 10);
    return intA - intB;
  };

  const sortedSections = [...(allRevisionNodes || [])].sort((a, b) => {
    const orderA = a.sibling_order || a.procedure_tree?.sibling_order;
    const orderB = b.sibling_order || b.procedure_tree?.sibling_order;
    const orderCompare = compareSiblingOrders(orderA, orderB);
    if (orderCompare !== 0) return orderCompare;
    const dateA = new Date(a.created_at);
    const dateB = new Date(b.created_at);
    return dateB - dateA;
  });

  const sectionIndex = sortedSections.findIndex(
    (section) => section.tree_node_id === section_id_ref
  );

  const stepIndex =
    sectionData?.steps?.findIndex(
      (step) => step.tree_node_id === step_id_ref
    ) || 0;

  // Calculate global step number
  const globalStepStart = sortedSections
    .slice(0, sectionIndex)
    .reduce((total, section) => total + (section.steps?.length || 0), 0);
  const globalStepNumber = globalStepStart + stepIndex + 1;

  const nodeNumber =
    computeGlobalMediaOrdinalForChild({
      allRevisionNodes,
      section_id_ref,
      step_id_ref,
      child_node_id,
      nodeType: enhancedChildNodeData?.type,
    }) ?? 1;

  const nodeTypeName = getNodeTypeName(enhancedChildNodeData?.type);

  // Create the title that will be used in both AppBar and checkbox display
  const nodeTitle = `${nodeTypeName} ${enhancedChildNodeData.is_deleted ? "(Deleted)" : nodeNumber}`;

  const substepIndex = isSubstepChild
    ? stepData?.substeps?.findIndex((s) => s.tree_node_id === substep_id_ref) ||
    0
    : undefined;

  const substepLetter = isSubstepChild && substepIndex !== undefined
    ? String.fromCharCode(65 + substepIndex)
    : undefined;

  const parentContext = {
    sectionIndex,
    stepIndex,
    globalStepNumber,
    substepIndex,
    substepLetter,
    sectionId: section_id_ref,
    stepId: step_id_ref,
    substepId: substep_id_ref,
  };

  const checkboxStateForChild = {
    currentNodeId: child_node_id,
    currentNodeType: enhancedChildNodeData.type,
    allChildrenChecked: true,
  };

  // Create enhanced child node data with the consistent title and node number
  const enhancedChildNodeDataWithTitle = {
    ...enhancedChildNodeData,
    displayTitle: nodeTitle, // Add the title for consistent display
    overrideNodeNumber: nodeNumber, // Pass the calculated node number to override ChildrenRenderer's calculation
  };

  return (
    <>
      <NodeAppBar
        title={nodeTitle}
        isSectionElement={true}
        isSelectedPage={true}
        onAddElement={onAddElement}
        onEditSectionElement={onEditSectionElement}
        partNumber={partnumber}
        revision={revision}
        partId={partId}
        revisionId={revisionId}
        sectionId={section_id_ref}
        sectionIndex={sectionIndex}
        stepId={step_id_ref}
        stepIndex={stepIndex}
        globalStepNumber={globalStepNumber}
        substepId={substep_id_ref}
        substepIndex={substepIndex}
        substepLetter={substepLetter}
        ltree_path={enhancedChildNodeData?.ltree_path}
        is_deleted={enhancedChildNodeData?.is_deleted || false}
        isLocked={isLock || (!bypassTabOrderLock && !isFirstTabByOrder) || (isApiLocked && lockedBy)}
        versionHistoryFallbackVersionId={
          enhancedChildNodeData?.detail?.version_id_ref ||
          enhancedChildNodeData?.version_id_ref
        }
        versionHistoryNodeId={
          enhancedChildNodeData?.tree_node_id || child_node_id
        }
        versionHistoryNodeIdField={versionHistoryNodeIdFieldForChildType(
          enhancedChildNodeData?.type,
        )}
        versionHistoryMatchIds={childVersionHistoryMatchIds}
        versionHistoryLeafMediaOrdinal={childMediaOrdinalForNodeHistory}
        versionHistoryLeafMediaPhraseRegex={
          childLeafMediaPhraseRegexForNodeHistory
        }
      />

      <div style={{ padding: "20px" }}>
        {/* Render the child node using ChildrenRenderer with overridden node number */}
        <ChildrenRenderer
          children={[enhancedChildNodeDataWithTitle]}
          parentContext={parentContext}
          onNavigate={onNavigate}
          checkboxState={checkboxStateForChild}
          allNodesData={allRevisionNodes}
          overrideNodeNumbers={[nodeNumber]} // Pass the correct node number to override calculation
        />
      </div>
    </>
  );
};

export default ViewChildNode;
